// Main app logic — AuthGate + auth initialization
import { router } from '@lib/dom/router.js';
import { authService } from '@features/auth/auth.service.js';
import AuthGate from '@features/auth/components/AuthGate.js';
import { h } from '@lib/dom/createElement.js';
import { getLocaleFromPath, initLocale, localizedPath, stripLocaleFromPath } from '@lib/i18n/i18n.js';

/**
 * Initialize the application
 */
export async function initApp() {
  const appEl = document.getElementById('app');

  if (!appEl) {
    console.error('App root element not found');
    return;
  }

  const checkoutReturn = sessionStorage.getItem('checkout_return_url');
  const currentRoutePath = stripLocaleFromPath(window.location.pathname);
  if (checkoutReturn) {
    sessionStorage.removeItem('checkout_return_url');
    sessionStorage.removeItem('modal_return_url');
    // Never override Lemon success callback route with stale checkout return data.
    if (currentRoutePath !== '/success') {
      try {
        const returnUrl = new URL(checkoutReturn, window.location.origin);
        const target = returnUrl.pathname + returnUrl.search;
        if (target !== window.location.pathname + window.location.search) {
          // Force a full app bootstrap on the intended return URL.
          window.location.replace(target);
          return;
        }
      } catch {
        // Fallback to authenticated home tab with current locale when stored URL is malformed.
        const fallback = localizedPath('/library');
        if (fallback !== window.location.pathname + window.location.search) {
          window.location.replace(fallback);
          return;
        }
      }
    }
  }

  const initialPath = window.location.pathname;
  const activeLocale = initLocale(initialPath);
  const localeFromPath = getLocaleFromPath(initialPath);
  if (!localeFromPath || localeFromPath !== activeLocale) {
    const localized = localizedPath(stripLocaleFromPath(initialPath), activeLocale);
    window.history.replaceState({}, '', localized);
  }
  // Setup routes
  setupRoutes();

  // The router will render pages into a container.
  // Wrap everything with AuthGate so the UI is blocked until auth is initialized.
  const routerRoot = h('div', { id: 'router-root' });

  // AuthGate shows a loading spinner until authService.init() finishes
  const gate = AuthGate(routerRoot);
  appEl.appendChild(gate);

  // Initialize auth (restores session, verifies with Supabase, then sets isInitialized=true)
  // MUST await so AuthGate swaps routerRoot into the DOM before router.start() looks for it
  await authService.init();

  // Start router (renders into #router-root — now guaranteed to be in the DOM)
  router.start('#router-root');
}

/**
 * Setup application routes
 */
function setupRoutes() {
  router.addRoute('/', () => import('@features/home/pages/HomePage.js'));
  router.addRoute('/guest', () => import('@features/home/pages/HomePage.js'));
  router.addRoute('/library', () => import('@features/home/pages/HomePage.js'));
  router.addRoute('/checkout', () => import('@features/home/pages/HomePage.js'));
  router.addRoute('/success', () => import('@features/home/pages/HomePage.js'));
  router.addRoute('/Offer1', () => import('@features/offer/pages/Offer1Page.js'));
  router.addRoute('/offer1', () => import('@features/offer/pages/Offer1Page.js'));
  // All auth routes (login, register, forgot-password) are modal-only
  // Profile and Paywall routes are modal-only
}
